import React, { useState, useEffect } from 'react';
import { TagsInput } from './TagsInput';
import { ContentBlockEditor } from './ContentBlockEditor';
import type { Template, TemplateBlock } from '../../../lib/types/template.types';

export interface TemplateFormData {
  name: string;
  description?: string;
  category_id?: string;
  blocks: TemplateBlock[];
  tags: string[];
  is_favorite: boolean;
}

interface TemplateFormProps {
  initialData?: Template;
  onSubmit: (data: TemplateFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  submitLabel?: string;
}

interface FormErrors {
  name?: string;
  blocks?: string;
}

export function TemplateForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = 'Create Template',
}: TemplateFormProps) {
  const [formData, setFormData] = useState<TemplateFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category_id: initialData?.category_id,
    blocks: initialData?.content.blocks || [],
    tags: initialData?.tags || [],
    is_favorite: initialData?.is_favorite || false,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Validate form
  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    } else if (formData.name.length > 200) {
      newErrors.name = 'Template name must be less than 200 characters';
    }

    if (formData.blocks.length === 0) {
      newErrors.blocks = 'At least one content block is required';
    } else {
      // Validate text blocks are not empty
      const hasEmptyTextBlock = formData.blocks.some(
        (block) => block.type === 'text' && !block.content.trim()
      );
      if (hasEmptyTextBlock) {
        newErrors.blocks = 'Text blocks cannot be empty';
      }

      // Validate image blocks have URLs
      const hasEmptyImageBlock = formData.blocks.some(
        (block) => block.type === 'image' && !block.storage_url
      );
      if (hasEmptyImageBlock) {
        newErrors.blocks = 'Image blocks must have an uploaded image';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Template Name */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Template Name
          <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Welcome Message, Follow-up Reminder"
          className={`w-full px-4 py-3 rounded-xl border ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          } focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all`}
          autoFocus
        />
        {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Description
          <span className="text-gray-400 text-xs ml-1">(optional)</span>
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of what this template is for..."
          rows={2}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-y"
        />
      </div>

      {/* Content Blocks */}
      <ContentBlockEditor
        blocks={formData.blocks}
        onChange={(blocks) => setFormData({ ...formData, blocks })}
        error={errors.blocks}
      />

      {/* Tags */}
      <TagsInput
        value={formData.tags}
        onChange={(tags) => setFormData({ ...formData, tags })}
      />

      {/* Favorite Toggle */}
      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
        <input
          type="checkbox"
          id="is-favorite"
          checked={formData.is_favorite}
          onChange={(e) => setFormData({ ...formData, is_favorite: e.target.checked })}
          className="w-5 h-5 text-[#5B8E87] border-gray-300 rounded focus:ring-[#5B8E87]"
        />
        <label htmlFor="is-favorite" className="text-sm font-medium text-gray-700 cursor-pointer">
          Mark as favorite
        </label>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 px-6 py-3 bg-[#5B8E87] text-white rounded-xl hover:bg-[#4F7A74] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );
}
